import React, { useContext } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import './tab-bar.scss';

import Tooltip from '../Tooltip/Tooltip';

import { TabsContext } from './TabbedPage';

export type TabProps = {
  label: string;
  selected?: boolean;
  onClick?: () => void;
  disabledTooltip?: string;
  disabled?: boolean;
};

export const Tab: React.FC<TabProps> = (props: TabProps) => {
  const { label, selected, onClick, disabledTooltip, disabled } = props;
  const tab = (
    <div
      onClick={!disabled ? onClick : undefined}
      className={classNames('dragonfruit-tab', {
        'dragonfruit-tab-selected': selected && !disabled,
        'dragonfruit-tab-disabled': disabled,
      })}
      // TODO: Remove data-testid and use getByRole('tab') instead
      data-testid="tabbed-page-tab"
      role="tab"
      aria-selected={selected}
    >
      {label}
    </div>
  );

  if (disabledTooltip) {
    return <Tooltip tooltip={disabledTooltip}>{tab}</Tooltip>;
  }

  return tab;
};

export interface DisabledTab {
  /** The id of the tab to disable. */
  label: string;
  tooltip?: string;
}

type TabBarProps = {
  disabledTabs?: DisabledTab[];
  className?: string;
  children?: React.ReactNode;
};

const TabBar: React.FC<TabBarProps> = (props) => {
  const { disabledTabs, className, children } = props;
  const tabs = useContext(TabsContext);

  async function onClickTab(label: string): Promise<void> {
    const onUnfocus = tabs?.getOnUnfocusCb(tabs.currentTabLabel);
    if (onUnfocus) {
      const shouldNavigate = await onUnfocus();
      if (!shouldNavigate) {
        return;
      }
    }

    tabs?.goToTab(label);
  }

  return (
    <div
      className={classNames('dragonfruit-tab-bar', className)}
      data-testid="tab-bar"
      // TODO: Consider using role="tabpanel"
      role="tablist"
    >
      {tabs?.allTabIds.map((key) => {
        const label = tabs.getTabLabel(key);
        const disabledTab = disabledTabs?.find((tab) => tab.label === key);
        return (
          <Tab
            key={key}
            label={label}
            onClick={(): Promise<void> => onClickTab(key)}
            selected={key === tabs.currentTabLabel}
            disabledTooltip={disabledTab?.tooltip}
            disabled={!!disabledTab}
          />
        );
      })}
      {children}
    </div>
  );
};

export default observer(TabBar);
